import { Prospect, TProspectInsurance } from 'types/common';
import {
    EEligibilityStatus,
    TBackendEligibilityCheckVo,
    ELIGIBILITY_TAG_BY_BACKEND_STATUS,
    ELIGIBILITY_TIP_BY_BACKEND_STATUS,
    headlineToken,
} from 'types/insuranceEligibility';

// 渲染时按行计算 eligibility 展示数据：列表/详情接口在行上下发 latestEligibilityCheck
// （只填 status + latestCheckedAt），这里转成前端展示词汇表。状态一律来自后端，前端不派生。

export type TClientEligibilityView = {
    status: EEligibilityStatus;
    lastCheckedAt?: string;
    // 查过就用被查那条 record 的 carrier 快照，没查过才回退档案第一份保险。
    // 患者换卡后档案 carrier 已是新保司，而状态说的是旧那次——两半拼在一起会读成
    // 「新保司已验证通过」（2026-09-16 拍板：列表不再显示多保险 +1 角标）
    carrier?: string;
    // 同一标签按成因换 hover 解释（如 NO_COVERAGE_DETAIL 的 Could not verify）
    tipOverride?: string;
};

// 列表行 / GP 详情的每位 provider 卡片共用同一套派生：入参只有「这一次 check 的摘要」与
// 「这条记录有没有保险」，两处对同一份数据不能给出不同说法
export const deriveEligibilityView = (
    vo: TBackendEligibilityCheckVo | undefined,
    hasInsurance: boolean,
    carrierOnFile?: string,
): TClientEligibilityView => {
    const lastCheckedAt = vo?.latestCheckedAt || undefined;
    // 多状态串按原型 tagState 的顺序挑头条（不是 tokens[0]）：列表与详情对同一份数据
    // 不能给出不同说法
    const primary = headlineToken(
        (vo?.status || '')
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean),
    );

    // 这一列回答的是「最后一次检查的结果」（2026-09-17 拍板）：查过就报那次的结果，
    // 哪怕那条保险之后被删了——No insurance / Never checked 只是「没有结果可报」时的两种说法。
    // 只显示后端有的状态，不再前端派生 7 天 stale 的 Needs re-check
    let status: EEligibilityStatus;
    if (primary) {
        status = ELIGIBILITY_TAG_BY_BACKEND_STATUS[primary] || EEligibilityStatus.CHECKED;
    } else if (hasInsurance) {
        status = EEligibilityStatus.NEVER_CHECKED;
    } else {
        status = EEligibilityStatus.NO_INSURANCE;
    }

    // 保司名跟着状态走：报的是某次检查的结果就用那次的 carrier 快照；没有结果可报时
    // 才退到档案上的保司（无保险则不印）
    const view: TClientEligibilityView = { status, lastCheckedAt };
    view.carrier = vo?.carrier || (hasInsurance ? carrierOnFile : undefined);
    if (primary) view.tipOverride = ELIGIBILITY_TIP_BY_BACKEND_STATUS[primary];
    return view;
};

// ---------- 多保险：primary / secondary 分组 ----------
// 老数据没有 insuranceType：sort 1 当 primary，其余当 secondary（policy-holder 上线前一人最多两张）
const isPrimary = (ins: TProspectInsurance) =>
    ins.insuranceType === 'PRIMARY' || (!ins.insuranceType && ins.sort === 1);
const isSecondary = (ins: TProspectInsurance) =>
    ins.insuranceType === 'SECONDARY' || (!ins.insuranceType && ins.sort > 1);

// default 在前，其余按 sort。set-default 接口只翻 is_default 不动 sort，所以「第一张」必须按
// isDefault 认，不能按 sort——否则设完 default 后资料区和 check 都还停在旧那张
const defaultFirst = (list: TProspectInsurance[]) =>
    [...list].sort((a, b) => Number(!!b.isDefault) - Number(!!a.isDefault) || (a.sort || 0) - (b.sort || 0));

export const getPrimaryInsurances = (insurances?: TProspectInsurance[]): TProspectInsurance[] =>
    defaultFirst((insurances || []).filter(isPrimary));

// 编辑表单保存时这一组里哪张是 default：沿用加载时的 isDefault（Set as default 设的那张），
// 一组里没有（新加的第一张 / default 那张被删）才落到第一张；多张都带 true 时只认第一个
export const defaultIndexOf = (list: Array<{ isDefault?: boolean }>): number => {
    const i = list.findIndex((ins) => ins.isDefault);
    return i >= 0 ? i : 0;
};

export const getSecondaryInsurances = (insurances?: TProspectInsurance[]): TProspectInsurance[] =>
    defaultFirst((insurances || []).filter(isSecondary));

// 每张卡最后一次 check 的索引（eligibilityCheckCards 元素带 insuranceId；没查过的卡不在里面）
export const indexCheckCards = (
    cards?: TBackendEligibilityCheckVo[] | null,
): Map<number, TBackendEligibilityCheckVo> => {
    const map = new Map<number, TBackendEligibilityCheckVo>();
    (cards || []).forEach((c) => {
        if (c.insuranceId != null) map.set(c.insuranceId, c);
    });
    return map;
};

// 最近一次 check 落在哪张卡的兜底：latestEligibilityCheck.insuranceId 缺省时（老接口），按 latestCheckedAt 从 cards 里反推
export const latestCheckedInsuranceId = (cards?: TBackendEligibilityCheckVo[] | null): number | undefined => {
    let best: TBackendEligibilityCheckVo | undefined;
    (cards || []).forEach((c) => {
        if (c.insuranceId == null || !c.latestCheckedAt) return;
        if (!best || new Date(c.latestCheckedAt) > new Date(best.latestCheckedAt!)) best = c;
    });
    return best?.insuranceId ?? undefined;
};

const latestUploadedCarrier = (insurances?: TProspectInsurance[]): string | undefined => {
    if (!insurances?.length) return undefined;
    return insurances.reduce((a, b) => ((a.id || 0) >= (b.id || 0) ? a : b)).carrier || undefined;
};

export const getClientEligibility = (item: Prospect): TClientEligibilityView =>
    deriveEligibilityView(
        item.latestEligibilityCheck,
        (item.insurances?.length || 0) > 0,
        item.latestEligibilityCheck
            ? (getPrimaryInsurances(item.insurances)[0]?.carrier || item.insurances?.[0]?.carrier)
            : latestUploadedCarrier(item.insurances),
    );
